import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'

import { fetchDeviceSetting, printerLog } from '@/apis/aipass'
import { checkinEposPrinterIdStorage } from '@/constants/self-checkin-device-storage'
import { useEposPrinter } from '@/hooks/use-epos-printer'
import { errorHandler } from '@/libs/errors'
import serialNumberToHostName from '@/libs/epos-serial-number-to-host'
import { deviceSettingsType, SendType } from '@/models/device/device-setting'

export type SelfCheckinPrinterSetting = Pick<deviceSettingsType, 'id' | 'deviceName'> & {
  deviceHost: string | undefined
}

type Options = {
  // 未認証のセルフチェックイン端末からの呼び出しでのみ true を渡す。
  // fetchDeviceSetting は hotelId の有無で叩くAPIの認証方式が変わるため。
  isSelfCheckinTablet?: boolean
}

export const usePrinterSetting = (hotelId: string, options?: Options) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [printerSettings, setPrinterSettings] = useState<SelfCheckinPrinterSetting[]>([])
  const [selectedId, setSelectedId] = useState('')
  const eposPrinter = useEposPrinter()

  const fetchPrinterSettings = async () => {
    setIsLoading(true)
    try {
      const deviceSettings = (await fetchDeviceSetting(options?.isSelfCheckinTablet ? hotelId : undefined)) || []
      setPrinterSettings(
        await Promise.all([
          { id: '', deviceName: t('Dont choose'), deviceHost: undefined },
          ...deviceSettings
            .filter(device => device.sendType === SendType.selfCheckin)
            .map(async device => ({
              ...device,
              deviceHost: device.serialNumber ? await serialNumberToHostName(device.serialNumber) : device.ipAddress,
            })),
        ]),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const selectPrinter = async (device: SelfCheckinPrinterSetting) => {
    setSelectedId(device.id)
    localStorage.setItem(checkinEposPrinterIdStorage, device.id)
    if (!device.id) {
      return
    }

    let printerConnection
    try {
      setIsLoading(true)
      printerConnection = await eposPrinter.initialize({
        hotelId,
        connectIdKey: checkinEposPrinterIdStorage,
      })
      await eposPrinter.printText('Connection Success', printerConnection.printer, printerConnection.connection)
      notification.open({ message: 'プリンターの接続に成功しました' })
      await printerLog({ message: 'Connection Success' })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
      eposPrinter.disconnect(printerConnection?.connection, printerConnection?.printer)
    }
  }

  useEffect(() => {
    setSelectedId(localStorage.getItem(checkinEposPrinterIdStorage) || '')
    fetchPrinterSettings()
  }, [hotelId, options?.isSelfCheckinTablet])

  return {
    isLoading,
    printerSettings,
    selectedId,
    selectPrinter,
  }
}
